import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' };
};

const STATUS_COLORS = {
  Active: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Draft: { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
  'On Leave': { bg: '#fef9c3', text: '#854d0e', border: '#fde047' },
  'On Probation': { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' },
  Suspended: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  Resigned: { bg: '#fce7f3', text: '#9d174d', border: '#f9a8d4' },
  Terminated: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  Archived: { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },
};

const EMPLOYMENT_TYPES = ['Full-Time', 'Part-Time', 'Contract', 'Freelance', 'Intern'];
const COMPENSATION_TYPES = ['Monthly', 'Monthly + Commission', 'Hourly', 'Daily', 'Weekly', 'Per Project'];
const ATTENDANCE_MODES = ['Office', 'Remote', 'Hybrid', 'Field'];
const WORK_MODES = ['On-site', 'Remote', 'Hybrid'];
const GENDERS = ['Male', 'Female'];
const NATIONALITIES = ['Lebanese', 'Afghan', 'Albanian', 'Algerian', 'American', 'Argentine', 'Armenian', 'Australian', 'Bangladeshi', 'Belgian', 'Brazilian', 'British', 'Canadian', 'Chinese', 'Cypriot', 'Egyptian', 'Emirati', 'Ethiopian', 'Filipino', 'French', 'German', 'Greek', 'Indian', 'Indonesian', 'Iranian', 'Iraqi', 'Irish', 'Italian', 'Japanese', 'Jordanian', 'Kenyan', 'Kuwaiti', 'Moroccan', 'Nepalese', 'Nigerian', 'Omani', 'Pakistani', 'Palestinian', 'Qatari', 'Romanian', 'Russian', 'Saudi', 'South African', 'Spanish', 'Sri Lankan', 'Sudanese', 'Syrian', 'Tunisian', 'Turkish', 'Ukrainian', 'Yemeni'];
const DOC_CATEGORIES = ['ID Document', 'Contract', 'Certificate', 'Visa/Work Permit', 'Medical', 'Insurance', 'Tax', 'Other'];

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [tab, setTab] = useState('personal');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [hrProfiles, setHrProfiles] = useState([]);

  // Sub-data
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [documents, setDocuments] = useState([]);

  // Modals
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: '', effective_date: '', reason: '' });
  const [showDeptAssign, setShowDeptAssign] = useState(false);
  const [deptAssignForm, setDeptAssignForm] = useState({});
  const [showPosAssign, setShowPosAssign] = useState(false);
  const [posAssignForm, setPosAssignForm] = useState({});
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [docForm, setDocForm] = useState({});
  const [docFile, setDocFile] = useState(null);
  const [showRehire, setShowRehire] = useState(false);
  const [rehireForm, setRehireForm] = useState({});
  const [showArchive, setShowArchive] = useState(false);

  const handleAuthError = useCallback((status) => {
    if (status === 401 || status === 403) { navigate('/login'); return true; }
    return false;
  }, [navigate]);

  const fetchEmployee = useCallback(async () => {
    if (!companyId || !id) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}`, { headers: getAuthHeaders() });
      if (handleAuthError(res.status)) return;
      const data = await res.json();
      setEmployee(data.data);
      setForm(data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, id, handleAuthError]);

  const fetchLookups = useCallback(async () => {
    if (!companyId) return;
    try {
      const [dRes, pRes, hRes] = await Promise.all([
        fetch(`${API}/companies/${companyId}/hr-departments`, { headers: getAuthHeaders() }),
        fetch(`${API}/companies/${companyId}/hr-positions`, { headers: getAuthHeaders() }),
        fetch(`${API}/companies/${companyId}/hr-profiles`, { headers: getAuthHeaders() }),
      ]);
      const [dData, pData, hData] = await Promise.all([dRes.json(), pRes.json(), hRes.json()]);
      setDepartments((dData.data || []).filter(d => d.status !== 'Archived'));
      setPositions((pData.data || []).filter(p => p.status !== 'Archived'));
      setHrProfiles(hData.data || []);
    } catch (e) { console.error(e); }
  }, [companyId]);

  const fetchSalaryHistory = useCallback(async () => {
    if (!companyId || !id) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/salary-history`, { headers: getAuthHeaders() });
      if (res.ok) { const data = await res.json(); setSalaryHistory(data.data || []); }
    } catch (e) { console.error(e); }
  }, [companyId, id]);

  const fetchDocuments = useCallback(async () => {
    if (!companyId || !id) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/documents`, { headers: getAuthHeaders() });
      if (res.ok) { const data = await res.json(); setDocuments(data.data || []); }
    } catch (e) { console.error(e); }
  }, [companyId, id]);

  useEffect(() => { fetchEmployee(); fetchLookups(); }, [fetchEmployee, fetchLookups]);
  useEffect(() => {
    if (tab === 'salary') fetchSalaryHistory();
    if (tab === 'documents') fetchDocuments();
  }, [tab, fetchSalaryHistory, fetchDocuments]);

  // ── Save edits ──
  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      ['allowances', 'commissions', 'fixed_bonuses', 'recurring_deductions'].forEach(field => {
        if (typeof body[field] === 'string') {
          try { body[field] = JSON.parse(body[field]); } catch { body[field] = []; }
        }
      });
      // Remove relationship objects
      delete body.company; delete body.department; delete body.position; delete body.hr_profile;
      delete body.department_assignments; delete body.position_assignments;
      delete body.salary_history; delete body.documents; delete body.employment_periods;
      delete body.effective_hr_profile; delete body.effective_hr_profile_source;

      const res = await fetch(`${API}/companies/${companyId}/employees/${id}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (handleAuthError(res.status)) return;
      const data = await res.json();
      if (res.ok) { setEmployee(data.data); setForm(data.data); setEditing(false); }
      else {
        if (data.requires_override) {
          const reason = window.prompt(`Headcount exceeded. Enter override reason:`);
          if (reason) {
            body.override_headcount = true;
            body.override_reason = reason;
            const res2 = await fetch(`${API}/companies/${companyId}/employees/${id}`, {
              method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body),
            });
            const data2 = await res2.json();
            if (res2.ok) { setEmployee(data2.data); setForm(data2.data); setEditing(false); }
            else alert(data2.message);
          }
        } else alert(data.message || 'Error saving');
      }
    } catch (e) { alert('Network error'); console.error(e); }
    setSaving(false);
  };

  // ── Change Status ──
  const handleChangeStatus = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/change-status`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(statusForm),
      });
      if (handleAuthError(res.status)) return;
      const data = await res.json();
      if (res.ok) { setShowStatusModal(false); fetchEmployee(); }
      else alert(data.message);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  // ── Department Assignment ──
  const handleAssignDept = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/departments`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(deptAssignForm),
      });
      if (res.ok) { setShowDeptAssign(false); fetchEmployee(); }
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleUnassignDept = async (deptId) => {
    if (!window.confirm('Remove this department assignment?')) return;    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/departments/${deptId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (res.ok) fetchEmployee();
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
  };

  // ── Position Assignment ──
  const handleAssignPos = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/positions`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(posAssignForm),
      });
      if (res.ok) { setShowPosAssign(false); fetchEmployee(); }
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleUnassignPos = async (posId) => {
    if (!window.confirm('Remove this position assignment?')) return;    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/positions/${posId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (res.ok) fetchEmployee();
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
  };

  // ── Document Upload ──
  const handleUploadDoc = async () => {
    if (!docFile) { alert('Please select a file'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', docFile);
      Object.entries(docForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        body: fd,
      });
      if (res.ok) { setShowDocUpload(false); setDocForm({}); setDocFile(null); fetchDocuments(); }
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/documents/${docId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (res.ok) fetchDocuments();
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
  };

  // ── Archive ──
  const handleArchive = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/archive`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({}),
      });
      if (res.ok) { setShowArchive(false); fetchEmployee(); }
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  // ── Rehire ──
  const handleRehire = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${id}/rehire`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(rehireForm),
      });
      if (res.ok) { setShowRehire(false); fetchEmployee(); }
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  // ── Styles ──
  const s = {
    page: { padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#1f2937', minHeight: '100vh', background: '#f9fafb' },
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
    backBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#2563eb', fontWeight: 600 },
    headerCard: { background: '#fff', borderRadius: 14, padding: '20px 28px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 },
    empInfo: { display: 'flex', alignItems: 'center', gap: 16 },
    avatar: { width: 56, height: 56, borderRadius: '50%', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#3730a3' },
    empName: { fontSize: 20, fontWeight: 700, margin: 0 },
    empSub: { fontSize: 13, color: '#6b7280', margin: 0 },
    badge: (colors) => ({ display: 'inline-block', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: colors?.bg || '#f3f4f6', color: colors?.text || '#374151', border: `1px solid ${colors?.border || '#d1d5db'}` }),
    actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    btn: { padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, transition: 'all 0.15s' },
    btnPrimary: { background: '#2563eb', color: '#fff' },
    btnDanger: { background: '#ef4444', color: '#fff' },
    btnOutline: { background: '#fff', color: '#374151', border: '1px solid #d1d5db' },
    btnSuccess: { background: '#16a34a', color: '#fff' },
    btnWarning: { background: '#f59e0b', color: '#fff' },
    tabs: { display: 'flex', gap: 0, background: '#fff', borderRadius: '14px 14px 0 0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
    tab: (active) => ({ padding: '12px 20px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400, color: active ? '#2563eb' : '#6b7280', borderBottom: active ? '3px solid #2563eb' : '3px solid transparent', background: active ? '#eff6ff' : '#fff', transition: 'all 0.15s', whiteSpace: 'nowrap' }),
    content: { background: '#fff', borderRadius: '0 0 14px 14px', padding: '24px 28px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', minHeight: 300 },
    section: { marginBottom: 24 },
    sectionTitle: { fontSize: 15, fontWeight: 700, marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 },
    field: { marginBottom: 8 },
    fieldLabel: { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
    fieldValue: { fontSize: 14, fontWeight: 500, marginTop: 2 },
    input: { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
    textarea: { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', minHeight: 60, resize: 'vertical', fontFamily: 'inherit' },
    select: { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', background: '#fff' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb', background: '#f9fafb' },
    td: { padding: '8px 12px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' },
    card: { background: '#f9fafb', borderRadius: 10, padding: '14px 18px', border: '1px solid #e5e7eb', marginBottom: 10 },
    cardRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
    modal: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 540, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
    modalHeader: { padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: 17, fontWeight: 700, margin: 0 },
    modalBody: { padding: '20px 24px' },
    modalFooter: { padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 },
    formGroup: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
    timeline: { borderLeft: '3px solid #e5e7eb', paddingLeft: 20, marginLeft: 10 },
    timelineItem: { position: 'relative', marginBottom: 20, paddingBottom: 4 },
    timelineDot: { position: 'absolute', left: -28, top: 4, width: 12, height: 12, borderRadius: '50%', border: '3px solid #2563eb', background: '#fff' },
    profileSource: { display: 'inline-block', padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600 },
    removeBtn: { background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  };

  // ── Field display/edit helpers ──
  const Field = ({ label, value, field, type = 'text', options, editable = true }) => {
    if (editing && editable) {
      if (type === 'select') {
        return (
          <div style={s.field}>
            <div style={s.fieldLabel}>{label}</div>
            <select style={s.select} value={form[field] || ''} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}>
              <option value="">—</option>
              {(options || []).map(o => typeof o === 'string'
                ? <option key={o} value={o}>{o}</option>
                : <option key={o.value} value={o.value}>{o.label}</option>
              )}
            </select>
          </div>
        );
      }
      if (type === 'textarea') {
        return (
          <div style={s.field}>
            <div style={s.fieldLabel}>{label}</div>
            <textarea style={s.textarea} value={form[field] || ''} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} />
          </div>
        );
      }
      return (
        <div style={s.field}>
          <div style={s.fieldLabel}>{label}</div>
          <input style={s.input} type={type} value={form[field] || ''} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} />
        </div>
      );
    }
    return (
      <div style={s.field}>
        <div style={s.fieldLabel}>{label}</div>
        <div style={s.fieldValue}>{value || '—'}</div>
      </div>
    );
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const formatMoney = (v) => v ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—';

  if (initialLoading) return <div style={s.page}><p>Loading…</p></div>;
  if (!employee) return <div style={s.page}><p>Employee not found.</p></div>;

  const sc = STATUS_COLORS[employee.status] || STATUS_COLORS.Draft;
  const initials = `${(employee.first_name || '')[0] || ''}${(employee.last_name || '')[0] || ''}`.toUpperCase();

  // ── Tab Renderers ──
  const renderPersonal = () => (
    <div>
      <div style={s.sectionTitle}>Personal Information</div>
      <div style={s.grid}>
        <Field label="First Name" value={employee.first_name} field="first_name" />
        <Field label="Middle Name" value={employee.middle_name} field="middle_name" />
        <Field label="Last Name" value={employee.last_name} field="last_name" />
        <Field label="Email" value={employee.email} field="email" type="email" />
        <Field label="Phone" value={employee.phone} field="phone" />
        <Field label="Date of Birth" value={formatDate(employee.date_of_birth)} field="date_of_birth" type="date" />
        <Field label="Gender" value={employee.gender} field="gender" type="select" options={GENDERS} />
        <Field label="Nationality" value={employee.nationality} field="nationality" type="select" options={[...new Set([...(employee.nationality && !NATIONALITIES.includes(employee.nationality) ? [employee.nationality] : []), ...NATIONALITIES])]} />
        <Field label="National ID" value={employee.national_id} field="national_id" />
      </div>
      <div style={{ ...s.sectionTitle, marginTop: 24 }}>Contact & Emergency</div>
      <div style={s.grid}>
        <Field label="Address" value={employee.address} field="address" type="textarea" />
        <Field label="Emergency Contact" value={employee.emergency_contact_name} field="emergency_contact_name" />
        <Field label="Emergency Phone" value={employee.emergency_contact_phone} field="emergency_contact_phone" />
      </div>
      <div style={{ ...s.sectionTitle, marginTop: 24 }}>Notes</div>
      <Field label="Notes" value={employee.notes} field="notes" type="textarea" />
    </div>
  );

  const renderEmployment = () => (
    <div>
      <div style={s.sectionTitle}>Employment Details</div>
      <div style={s.grid}>
        <Field label="Employee Code" value={employee.employee_code} field="employee_code" editable={false} />
        <Field label="Status" value={employee.status} field="status" editable={false} />
        <Field label="Employment Type" value={employee.employment_type} field="employment_type" type="select" options={EMPLOYMENT_TYPES} />
        <Field label="Compensation Type" value={employee.compensation_type} field="compensation_type" type="select" options={COMPENSATION_TYPES} />
        <Field label="Attendance Mode" value={employee.attendance_mode} field="attendance_mode" type="select" options={ATTENDANCE_MODES} />
        <Field label="Work Mode" value={employee.work_mode} field="work_mode" type="select" options={WORK_MODES} />
        <Field label="Hire Date" value={formatDate(employee.hire_date)} field="hire_date" type="date" />
        <Field label="End Date" value={formatDate(employee.end_date)} field="end_date" type="date" />
        <Field label="Probation (days)" value={employee.probation_period} field="probation_period" type="number" />
        <Field label="Notice Period (days)" value={employee.notice_period} field="notice_period" type="number" />
      </div>
      <div style={{ marginTop: 20 }}>
        <button style={{ ...s.btn, ...s.btnWarning }} onClick={() => { setStatusForm({ status: '', effective_date: new Date().toISOString().split('T')[0], reason: '' }); setShowStatusModal(true); }}>
          🔄 Change Status
        </button>
      </div>
    </div>
  );

  const renderDeptPosition = () => (
    <div>
      <div style={s.sectionTitle}>
        Department Assignments
        <button style={{ ...s.btn, ...s.btnPrimary, float: 'right', fontSize: 12, padding: '4px 12px' }}
          onClick={() => { setDeptAssignForm({}); setShowDeptAssign(true); }}>+ Add Department</button>
      </div>
      {(employee.department_assignments || []).length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: 13 }}>No department assignments.</p>
      ) : (employee.department_assignments || []).map(da => (
        <div key={da.id} style={s.card}>
          <div style={s.cardRow}>
            <div>
              <strong>{da.department?.name || 'Unknown'}</strong>
              {da.is_primary && <span style={{ ...s.badge({ bg: '#dcfce7', text: '#166534', border: '#86efac' }), marginLeft: 8, fontSize: 10 }}>PRIMARY</span>}
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Since: {formatDate(da.effective_date)}
                {!da.is_primary && ` · Shared: ${['salary', 'attendance', 'leave', 'hr_profile', 'transportation', 'work_schedule'].filter(k => da[`shared_${k}`]).join(', ') || 'none'}`}
              </div>
            </div>
            {!da.is_primary && (
              <button style={s.removeBtn} onClick={() => handleUnassignDept(da.department_id)}>✕ Remove</button>
            )}
          </div>
        </div>
      ))}

      <div style={{ ...s.sectionTitle, marginTop: 28 }}>
        Position Assignments
        <button style={{ ...s.btn, ...s.btnPrimary, float: 'right', fontSize: 12, padding: '4px 12px' }}
          onClick={() => { setPosAssignForm({}); setShowPosAssign(true); }}>+ Add Position</button>
      </div>
      {(employee.position_assignments || []).length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: 13 }}>No position assignments.</p>
      ) : (employee.position_assignments || []).map(pa => (
        <div key={pa.id} style={s.card}>
          <div style={s.cardRow}>
            <div>
              <strong>{pa.position?.title || 'Unknown'}</strong>
              {pa.is_primary && <span style={{ ...s.badge({ bg: '#dcfce7', text: '#166534', border: '#86efac' }), marginLeft: 8, fontSize: 10 }}>PRIMARY</span>}
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Since: {formatDate(pa.effective_date)}</div>
            </div>
            {!pa.is_primary && (
              <button style={s.removeBtn} onClick={() => handleUnassignPos(pa.position_id)}>✕ Remove</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderHrProfile = () => {
    const ep = employee.effective_hr_profile;
    const source = employee.effective_hr_profile_source;
    const sourceColors = {
      Employee: { bg: '#dcfce7', text: '#166534' },
      Department: { bg: '#e0e7ff', text: '#3730a3' },
      Inherited: { bg: '#fef9c3', text: '#854d0e' },
      None: { bg: '#fee2e2', text: '#991b1b' },
    };
    const sc2 = sourceColors[source] || sourceColors.None;

    return (
      <div>
        <div style={s.sectionTitle}>Effective HR Profile</div>
        <div style={{ ...s.card, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ ...s.profileSource, background: sc2.bg, color: sc2.text }}>Source: {source}</span>
            {ep && <strong style={{ fontSize: 15 }}>{ep.name}</strong>}
          </div>
          {ep ? (
            <div style={s.grid}>
              <div style={s.field}><div style={s.fieldLabel}>Work Days/Week</div><div style={s.fieldValue}>{ep.work_days_per_week || '—'}</div></div>
              <div style={s.field}><div style={s.fieldLabel}>Hours/Day</div><div style={s.fieldValue}>{ep.work_hours_per_day || '—'}</div></div>
              <div style={s.field}><div style={s.fieldLabel}>Overtime Rate</div><div style={s.fieldValue}>{ep.overtime_rate || '—'}</div></div>
              <div style={s.field}><div style={s.fieldLabel}>Annual Leave</div><div style={s.fieldValue}>{ep.annual_leave_days || '—'} days</div></div>
              <div style={s.field}><div style={s.fieldLabel}>Sick Leave</div><div style={s.fieldValue}>{ep.sick_leave_days || '—'} days</div></div>
            </div>
          ) : (
            <p style={{ color: '#ef4444', fontSize: 13 }}>⚠️ No HR profile is assigned or inherited. Assign one to this employee or their department.</p>
          )}
        </div>
        <div style={s.sectionTitle}>Override HR Profile</div>
        {editing ? (
          <Field label="Employee-Level HR Profile" value="" field="hr_profile_id" type="select"
            options={hrProfiles.map(p => ({ value: p.id, label: p.name }))} />
        ) : (
          <div style={s.field}>
            <div style={s.fieldLabel}>Employee-Level Override</div>
            <div style={s.fieldValue}>{employee.hr_profile?.name || 'None (using inherited)'}</div>
          </div>
        )}
      </div>
    );
  };

  const renderSalary = () => (
    <div>
      <div style={s.sectionTitle}>Current Compensation</div>
      <div style={s.grid}>
        <Field label="Base Salary" value={formatMoney(employee.salary)} field="salary" type="number" />
        <Field label="Transportation Rate" value={formatMoney(employee.transportation_rate)} field="transportation_rate" type="number" />
      </div>
      <div style={s.grid}>
        <div style={s.field}>
          <div style={s.fieldLabel}>Allowances</div>
          <div style={s.fieldValue}>
            {Array.isArray(employee.allowances) && employee.allowances.length > 0
              ? employee.allowances.map((a, i) => <span key={i} style={{ ...s.badge({ bg: '#dcfce7', text: '#166534', border: '#86efac' }), marginRight: 6 }}>{a.name}: ${a.amount}</span>)
              : '—'}
          </div>
        </div>
        <div style={s.field}>
          <div style={s.fieldLabel}>Commissions</div>
          <div style={s.fieldValue}>
            {Array.isArray(employee.commissions) && employee.commissions.length > 0
              ? employee.commissions.map((c, i) => <span key={i} style={{ ...s.badge({ bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' }), marginRight: 6 }}>{c.name}: {c.rate}%</span>)
              : '—'}
          </div>
        </div>
        <div style={s.field}>
          <div style={s.fieldLabel}>Fixed Bonuses</div>
          <div style={s.fieldValue}>
            {Array.isArray(employee.fixed_bonuses) && employee.fixed_bonuses.length > 0
              ? employee.fixed_bonuses.map((b, i) => <span key={i} style={{ ...s.badge({ bg: '#fef9c3', text: '#854d0e', border: '#fde047' }), marginRight: 6 }}>{b.name}: ${b.amount}</span>)
              : '—'}
          </div>
        </div>
        <div style={s.field}>
          <div style={s.fieldLabel}>Recurring Deductions</div>
          <div style={s.fieldValue}>
            {Array.isArray(employee.recurring_deductions) && employee.recurring_deductions.length > 0
              ? employee.recurring_deductions.map((d, i) => <span key={i} style={{ ...s.badge({ bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' }), marginRight: 6 }}>{d.name}: ${d.amount}</span>)
              : '—'}
          </div>
        </div>
      </div>

      <div style={{ ...s.sectionTitle, marginTop: 28 }}>Salary History</div>
      {salaryHistory.length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: 13 }}>No salary changes recorded.</p>
      ) : (
        <div style={s.timeline}>
          {salaryHistory.map(h => (
            <div key={h.id} style={s.timelineItem}>
              <div style={s.timelineDot} />
              <div style={{ fontSize: 12, color: '#6b7280' }}>{formatDate(h.effective_date)}</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>
                {formatMoney(h.previous_salary)} → {formatMoney(h.new_salary)}
              </div>
              {h.reason && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Reason: {h.reason}</div>}
              {h.changed_by_user && <div style={{ fontSize: 11, color: '#9ca3af' }}>By: {h.changed_by_user.name}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderDocuments = () => (
    <div>
      <div style={s.sectionTitle}>
        Documents
        <button style={{ ...s.btn, ...s.btnPrimary, float: 'right', fontSize: 12, padding: '4px 12px' }}
          onClick={() => { setDocForm({ category: '', title: '' }); setDocFile(null); setShowDocUpload(true); }}>+ Upload Document</button>
      </div>
      {documents.length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: 13 }}>No documents uploaded.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Title</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Issue Date</th>
                <th style={s.th}>Expiry Date</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => {
                const isExpiring = doc.expiry_date && new Date(doc.expiry_date) <= new Date(Date.now() + 30 * 86400000);
                const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();
                return (
                  <tr key={doc.id}>
                    <td style={s.td}>
                      {doc.title}
                      {doc.is_confidential && <span style={{ ...s.badge({ bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' }), marginLeft: 6, fontSize: 10 }}>🔒 Confidential</span>}
                    </td>
                    <td style={s.td}>{doc.category}</td>
                    <td style={s.td}>{formatDate(doc.issue_date)}</td>
                    <td style={{ ...s.td, color: isExpired ? '#ef4444' : isExpiring ? '#f59e0b' : 'inherit', fontWeight: isExpired || isExpiring ? 600 : 400 }}>
                      {formatDate(doc.expiry_date)} {isExpired ? '⚠️ EXPIRED' : isExpiring ? '⏰ EXPIRING' : ''}
                    </td>
                    <td style={s.td}>{doc.status}</td>
                    <td style={s.td}>
                      <button style={{ ...s.removeBtn }} onClick={() => handleDeleteDoc(doc.id)}>🗑️ Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderHistory = () => (
    <div>
      <div style={s.sectionTitle}>Employment Periods</div>
      {(employee.employment_periods || []).length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: 13 }}>No employment periods recorded.</p>
      ) : (
        <div style={s.timeline}>
          {(employee.employment_periods || []).map(p => (
            <div key={p.id} style={s.timelineItem}>
              <div style={{ ...s.timelineDot, borderColor: p.reason === 'rehire' ? '#16a34a' : '#2563eb' }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ ...s.badge(p.reason === 'rehire' ? { bg: '#dcfce7', text: '#166534', border: '#86efac' } : { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' }), fontSize: 10 }}>
                  Period #{p.period_number} — {p.reason === 'rehire' ? 'Rehire' : 'Initial'}
                </span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 500, marginTop: 4 }}>
                {formatDate(p.start_date)} → {p.end_date ? formatDate(p.end_date) : 'Present'}
              </div>
              {p.notes && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{p.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderActions = () => (
    <div>
      <div style={s.sectionTitle}>Employee Actions</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {employee.status !== 'Archived' && (
          <button style={{ ...s.btn, ...s.btnWarning }} onClick={() => setShowArchive(true)}>📦 Archive Employee</button>
        )}
        {employee.status === 'Archived' && (
          <button style={{ ...s.btn, ...s.btnSuccess }} onClick={() => {
            setRehireForm({ hire_date: new Date().toISOString().split('T')[0], department_id: '', position_id: '' });
            setShowRehire(true);
          }}>🔄 Rehire Employee</button>
        )}
        {employee.status !== 'Archived' && (
          <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => {
            setStatusForm({ status: '', effective_date: new Date().toISOString().split('T')[0], reason: '' });
            setShowStatusModal(true);
          }}>🔄 Change Status</button>
        )}
      </div>
      {employee.status === 'Archived' && (
        <div style={{ ...s.card, marginTop: 20, background: '#fef3c7', border: '1px solid #f59e0b' }}>
          <p style={{ margin: 0, fontSize: 13 }}>
            ⚠️ This employee is archived. Their data is preserved but they are excluded from active lists.
            Use <strong>Rehire</strong> to reactivate with a new employment period.
          </p>
        </div>
      )}
    </div>
  );

  const TABS = [
    { key: 'personal', label: '👤 Personal', render: renderPersonal },
    { key: 'employment', label: '💼 Employment', render: renderEmployment },
    { key: 'deptpos', label: '🏢 Dept & Position', render: renderDeptPosition },
    { key: 'hrprofile', label: '⚙️ HR Profile', render: renderHrProfile },
    { key: 'salary', label: '💰 Salary', render: renderSalary },
    { key: 'documents', label: '📄 Documents', render: renderDocuments },
    { key: 'history', label: '📅 History', render: renderHistory },
    { key: 'actions', label: '⚡ Actions', render: renderActions },
  ];

  return (
    <div style={s.page}>
      {/* Top Bar */}
      <div style={s.topBar}>
        <button style={s.backBtn} onClick={() => navigate('/company/employees')}>← Back to Employee Directory</button>
      </div>

      {/* Header Card */}
      <div style={s.headerCard}>
        <div style={s.empInfo}>
          <div style={s.avatar}>{initials}</div>
          <div>
            <h1 style={s.empName}>{employee.first_name} {employee.middle_name} {employee.last_name}</h1>
            <p style={s.empSub}>
              {employee.employee_code || 'No code'} · {employee.position?.title || 'No position'} · {employee.department?.name || 'No dept'}
            </p>
          </div>
        </div>
        <div style={s.actions}>
          <span style={s.badge(sc)}>{employee.status}</span>
          {editing ? (
            <>
              <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => { setEditing(false); setForm(employee); }}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleSave}>
                {saving ? 'Saving…' : '💾 Save Changes'}
              </button>
            </>
          ) : (
            <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => { setEditing(true); setForm(employee); }}>✏️ Edit</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {TABS.map(t => (
          <div key={t.key} style={s.tab(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</div>
        ))}
      </div>
      <div style={s.content}>
        {TABS.find(t => t.key === tab)?.render()}
      </div>

      {/* ── MODALS ── */}

      {/* Change Status Modal */}
      {showStatusModal && (
        <div style={s.overlay} onClick={() => setShowStatusModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>🔄 Change Status</h2>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }} onClick={() => setShowStatusModal(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>New Status *</label>
                <select style={s.select} value={statusForm.status} onChange={e => setStatusForm(p => ({ ...p, status: e.target.value }))}>
                  <option value="">— Select —</option>
                  {['Active', 'On Leave', 'On Probation', 'Suspended', 'Resigned', 'Terminated'].map(st =>
                    <option key={st} value={st}>{st}</option>
                  )}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Effective Date *</label>
                <input style={s.input} type="date" value={statusForm.effective_date} onChange={e => setStatusForm(p => ({ ...p, effective_date: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Reason</label>
                <textarea style={s.textarea} value={statusForm.reason} onChange={e => setStatusForm(p => ({ ...p, reason: e.target.value }))} />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => setShowStatusModal(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving || !statusForm.status}
                onClick={handleChangeStatus}>{saving ? 'Saving…' : 'Change Status'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Department Modal */}
      {showDeptAssign && (
        <div style={s.overlay} onClick={() => setShowDeptAssign(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>🏢 Assign Department</h2>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }} onClick={() => setShowDeptAssign(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Department *</label>
                <select style={s.select} value={deptAssignForm.department_id || ''} onChange={e => setDeptAssignForm(p => ({ ...p, department_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>
                  <input type="checkbox" checked={deptAssignForm.is_primary || false}
                    onChange={e => setDeptAssignForm(p => ({ ...p, is_primary: e.target.checked }))} />
                  {' '}Set as Primary Department
                </label>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Effective Date</label>
                <input style={s.input} type="date" value={deptAssignForm.effective_date || ''} onChange={e => setDeptAssignForm(p => ({ ...p, effective_date: e.target.value }))} />
              </div>
              <div style={{ ...s.sectionTitle, fontSize: 13, marginTop: 16 }}>Shared Settings</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {['salary', 'attendance', 'leave', 'hr_profile', 'transportation', 'work_schedule', 'employment_status', 'dates'].map(k => (
                  <label key={k} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={deptAssignForm[`shared_${k}`] !== false}
                      onChange={e => setDeptAssignForm(p => ({ ...p, [`shared_${k}`]: e.target.checked }))} />
                    {k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </label>
                ))}
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => setShowDeptAssign(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving || !deptAssignForm.department_id}
                onClick={handleAssignDept}>{saving ? 'Saving…' : 'Assign'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Position Modal */}
      {showPosAssign && (
        <div style={s.overlay} onClick={() => setShowPosAssign(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>📋 Assign Position</h2>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }} onClick={() => setShowPosAssign(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Position *</label>
                <select style={s.select} value={posAssignForm.position_id || ''} onChange={e => setPosAssignForm(p => ({ ...p, position_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {positions.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>
                  <input type="checkbox" checked={posAssignForm.is_primary || false}
                    onChange={e => setPosAssignForm(p => ({ ...p, is_primary: e.target.checked }))} />
                  {' '}Set as Primary Position
                </label>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Effective Date</label>
                <input style={s.input} type="date" value={posAssignForm.effective_date || ''} onChange={e => setPosAssignForm(p => ({ ...p, effective_date: e.target.value }))} />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => setShowPosAssign(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving || !posAssignForm.position_id}
                onClick={handleAssignPos}>{saving ? 'Saving…' : 'Assign'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showDocUpload && (
        <div style={s.overlay} onClick={() => setShowDocUpload(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>📄 Upload Document</h2>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }} onClick={() => setShowDocUpload(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Title *</label>
                <input style={s.input} value={docForm.title || ''} onChange={e => setDocForm(p => ({ ...p, title: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Category *</label>
                <select style={s.select} value={docForm.category || ''} onChange={e => setDocForm(p => ({ ...p, category: e.target.value }))}>
                  <option value="">— Select —</option>
                  {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>File *</label>
                <input type="file" onChange={e => setDocFile(e.target.files[0])} style={{ fontSize: 13 }} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Issue Date</label>
                <input style={s.input} type="date" value={docForm.issue_date || ''} onChange={e => setDocForm(p => ({ ...p, issue_date: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Expiry Date</label>
                <input style={s.input} type="date" value={docForm.expiry_date || ''} onChange={e => setDocForm(p => ({ ...p, expiry_date: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Notes</label>
                <textarea style={s.textarea} value={docForm.notes || ''} onChange={e => setDocForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={docForm.is_confidential || false}
                    onChange={e => setDocForm(p => ({ ...p, is_confidential: e.target.checked }))} />
                  🔒 Confidential
                </label>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => setShowDocUpload(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary, opacity: saving ? 0.6 : 1 }}
                disabled={saving || !docForm.title || !docForm.category || !docFile}
                onClick={handleUploadDoc}>{saving ? 'Uploading…' : 'Upload'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirm Modal */}
      {showArchive && (
        <div style={s.overlay} onClick={() => setShowArchive(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>📦 Archive Employee</h2>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }} onClick={() => setShowArchive(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ fontSize: 14 }}>
                Archive <strong>{employee.first_name} {employee.middle_name} {employee.last_name}</strong>? They will be removed from active lists but all data is preserved. You can rehire later.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => setShowArchive(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnWarning, opacity: saving ? 0.6 : 1 }} disabled={saving}
                onClick={handleArchive}>{saving ? 'Archiving…' : 'Archive'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Rehire Modal */}
      {showRehire && (
        <div style={s.overlay} onClick={() => setShowRehire(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>🔄 Rehire Employee</h2>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }} onClick={() => setShowRehire(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
                Rehiring creates a new employment period and reactivates the employee.
              </p>
              <div style={s.formGroup}>
                <label style={s.label}>New Hire Date *</label>
                <input style={s.input} type="date" value={rehireForm.hire_date || ''} onChange={e => setRehireForm(p => ({ ...p, hire_date: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Department *</label>
                <select style={s.select} value={rehireForm.department_id || ''} onChange={e => setRehireForm(p => ({ ...p, department_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Position *</label>
                <select style={s.select} value={rehireForm.position_id || ''} onChange={e => setRehireForm(p => ({ ...p, position_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {positions.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Salary</label>
                <input style={s.input} type="number" value={rehireForm.salary || ''} onChange={e => setRehireForm(p => ({ ...p, salary: e.target.value }))} />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => setShowRehire(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnSuccess, opacity: saving ? 0.6 : 1 }}
                disabled={saving || !rehireForm.hire_date || !rehireForm.department_id || !rehireForm.position_id}
                onClick={handleRehire}>{saving ? 'Processing…' : 'Rehire'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
